import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { CartProvider } from "@/contexts/CartContext";
import { MusicPlayerProvider } from "@/contexts/MusicPlayerContext";
import { CartDrawer } from "@/components/shop/CartDrawer";
import { HelmetProvider } from "react-helmet-async";
import { SmartInstallBanner } from "@/components/SmartInstallBanner";
import { IOSInstallPrompt } from "@/components/IOSInstallPrompt";
import { OfflineDetector } from "@/components/OfflineDetector";
import { BackgroundSyncProvider } from "@/components/BackgroundSyncProvider";
import { ErrorTriggerButton } from "@/components/ErrorBoundary";
import Index from "./pages/Index";

const Offline = lazy(() => import("./pages/Offline"));

// Lazy load non-critical routes for better performance
const RedBeanTanch = lazy(() => import("./pages/RedBeanTanch"));
const WillBarfield = lazy(() => import("./pages/WillBarfield"));
const LandisMaximus = lazy(() => import("./pages/LandisMaximus"));
const Careers = lazy(() => import("./pages/Careers"));
const Sponsors = lazy(() => import("./pages/Sponsors"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const YachtClub = lazy(() => import("./pages/YachtClub"));
const DivisionStreet = lazy(() => import("./pages/DivisionStreet"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const FanAuth = lazy(() => import("./pages/FanAuth"));
const FanPortal = lazy(() => import("./pages/FanPortal"));
const MailingListConfirmation = lazy(() => import("./pages/MailingListConfirmation"));
const AdminBootstrap = lazy(() => import("./pages/AdminBootstrap"));
const AdminRoles = lazy(() => import("./pages/AdminRoles"));
const MusicianVault = lazy(() => import("./pages/MusicianVault"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Schedule = lazy(() => import("./pages/Schedule"));
const Onboard = lazy(() => import("./pages/Onboard"));
const AdminOnboarding = lazy(() => import("./pages/AdminOnboarding"));
const Shop = lazy(() => import("./pages/Shop"));
const Checkout = lazy(() => import("./pages/Checkout"));
const AdminMerch = lazy(() => import("./pages/AdminMerch"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Tour = lazy(() => import("./pages/Tour"));
const StreamLanding = lazy(() => import("./pages/StreamLanding"));
const StreamWatch = lazy(() => import("./pages/StreamWatch"));
const AdminStreams = lazy(() => import("./pages/AdminStreams"));
const YachtClubDashboard = lazy(() => import("./pages/YachtClubDashboard"));
const AdminYachtClub = lazy(() => import("./pages/AdminYachtClub"));
const YachtClubVault = lazy(() => import("./pages/YachtClubVault"));
const AdminDivisionStreet = lazy(() => import("./pages/AdminDivisionStreet"));
const AdminComms = lazy(() => import("./pages/AdminComms"));
const FanAccount = lazy(() => import("./pages/FanAccount"));
const AdminBookings = lazy(() => import("./pages/AdminBookings"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <CartProvider>
          <MusicPlayerProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <OfflineDetector fallback={<Suspense fallback={<div className="min-h-screen bg-background" />}><Offline /></Suspense>}>
                <BackgroundSyncProvider>
                  <CartDrawer />
                  <SmartInstallBanner />
                  <IOSInstallPrompt />
                  <Suspense fallback={<div className="min-h-screen bg-background" />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/red-bean-tanch" element={<RedBeanTanch />} />
                      <Route path="/will-barfield" element={<WillBarfield />} />
                      <Route path="/landis-maximus" element={<LandisMaximus />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route path="/sponsors" element={<Sponsors />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/yacht-club" element={<YachtClub />} />
                      <Route path="/division-street" element={<DivisionStreet />} />
                      <Route path="/location/:locationId" element={<LocationPage />} />
                      <Route path="/fan-auth" element={<FanAuth />} />
                      <Route path="/fan-portal" element={<FanPortal />} />
                      <Route path="/mailing-list-confirmed" element={<MailingListConfirmation />} />
                      <Route path="/admin-bootstrap" element={<AdminBootstrap />} />
                      <Route path="/admin/roles" element={<AdminRoles />} />
                      <Route path="/admin/merch" element={<AdminMerch />} />
                      <Route path="/musician" element={<MusicianVault />} />
                      <Route path="/bookings" element={<Bookings />} />
                      <Route path="/schedule" element={<Schedule />} />
                      <Route path="/onboard" element={<Onboard />} />
                      <Route path="/admin/onboarding" element={<AdminOnboarding />} />
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="/shop" element={<Shop />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/tour" element={<Tour />} />
                      <Route path="/stream" element={<StreamLanding />} />
                      <Route path="/stream/watch/:code" element={<StreamWatch />} />
                      <Route path="/admin/streams" element={<AdminStreams />} />
                      <Route path="/yacht-club/dashboard" element={<YachtClubDashboard />} />
                      <Route path="/admin/yacht-club" element={<AdminYachtClub />} />
                      <Route path="/yacht-club/vault" element={<YachtClubVault />} />
                      <Route path="/admin/division-street" element={<AdminDivisionStreet />} />
                      <Route path="/admin/comms" element={<AdminComms />} />
                      <Route path="/admin/bookings" element={<AdminBookings />} />
                      <Route path="/account" element={<FanAccount />} />
                      <Route path="/offline" element={<Offline />} />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  <ErrorTriggerButton />
                </BackgroundSyncProvider>
              </OfflineDetector>
            </BrowserRouter>
          </MusicPlayerProvider>
        </CartProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
